import type { CSSProperties, FC, ReactNode } from 'react'

import { Fragment } from 'react'
import { cn } from '@/lib/utils'

type RevealTag = 'h1' | 'h2' | 'h3' | 'p' | 'span' | 'div'

interface RevealTextProps {
    text: string
    as?: RevealTag
    id?: string
    className?: string
    highlight?: Array<string>
    highlightClassName?: string
    delay?: number
}

const normalize = (word: string): string => word.toLowerCase().replace(/[^\p{L}\p{N}$%&+]/gu, '')

const findHighlighted = (words: Array<string>, phrases: Array<string>): Set<number> => {
    const marked = new Set<number>()
    const normalized = words.map(normalize)

    for (const phrase of phrases) {
        const parts = phrase.split(/\s+/).map(normalize).filter(Boolean)

        if (!parts.length) continue

        for (let start = 0; start <= normalized.length - parts.length; start++) {
            const matches = parts.every((part, offset) => normalized[start + offset] === part)

            if (!matches) continue

            for (let offset = 0; offset < parts.length; offset++) marked.add(start + offset)
        }
    }

    return marked
}

/**
 * Headline that reveals word by word when scrolled into view.
 * A newline in the text forces a line break. Words listed in
 * `highlight` (single words or phrases) get `highlightClassName`.
 */
export const RevealText: FC<RevealTextProps> = ({
    text,
    as: Tag = 'h2',
    id,
    className,
    highlight = [],
    highlightClassName,
    delay = 0
}): ReactNode => {
    const lines = text.split('\n')
    const allWords = lines.flatMap(line => line.split(' ').filter(Boolean))
    const marked = findHighlighted(allWords, highlight)

    let wordIndex = 0

    return (
        <Tag
            id={id}
            data-reveal='words'
            className={cn(className)}
            style={{ '--reveal-delay': `${delay}ms` } as CSSProperties}
        >
            {lines.map((line, lineIndex) => {
                const words = line.split(' ').filter(Boolean)

                return (
                    <Fragment key={lineIndex}>
                        {words.map((word, index) => {
                            const current = wordIndex++

                            return (
                                <Fragment key={`${lineIndex}-${index}`}>
                                    <span className='reveal-word'>
                                        <span
                                            className={marked.has(current) ? highlightClassName : undefined}
                                            style={{ '--word-index': current } as CSSProperties}
                                        >
                                            {word}
                                        </span>
                                    </span>

                                    {index < words.length - 1 && ' '}
                                </Fragment>
                            )
                        })}

                        {lineIndex < lines.length - 1 && <br />}
                    </Fragment>
                )
            })}
        </Tag>
    )
}
